import {
  type ChangeEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { MARKET_SNAPSHOT, RISKS, SITE_CONFIG } from "./config";
import { MemberExperience } from "./components/MemberExperience";
import { RebateProgram } from "./components/RebateProgram";
import {
  calculateOutcome,
  validateCalculatorInputs,
} from "./lib/calculator";

type IconName =
  | "arrow"
  | "book"
  | "calculator"
  | "check"
  | "clock"
  | "external"
  | "shield"
  | "trend"
  | "wallet"
  | "warning";

const iconPaths: Record<IconName, ReactNode> = {
  arrow: (
    <>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </>
  ),
  book: (
    <>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
    </>
  ),
  calculator: (
    <>
      <rect width="16" height="20" x="4" y="2" rx="2" />
      <path d="M8 6h8M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
    </>
  ),
  check: (
    <>
      <path d="M20 6 9 17l-5-5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  external: (
    <>
      <path d="M15 3h6v6M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </>
  ),
  shield: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  trend: (
    <>
      <path d="m3 17 6-6 4 4 8-8" />
      <path d="M15 7h6v6" />
    </>
  ),
  wallet: (
    <>
      <path d="M20 7V5a2 2 0 0 0-2-2H5a3 3 0 0 0 0 6h15v12H5a3 3 0 0 1-3-3V6" />
      <path d="M16 13h2" />
    </>
  ),
  warning: (
    <>
      <path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3Z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
};

function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {iconPaths[name]}
    </svg>
  );
}

function InviteLink({
  children,
  className = "primary-button",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <a
      className={className}
      href={SITE_CONFIG.inviteUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
    >
      {children}
    </a>
  );
}

function SectionHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="section-heading reveal">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {description && <p className="section-description">{description}</p>}
    </div>
  );
}

function formatUsdt(value: number) {
  return new Intl.NumberFormat("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [amount, setAmount] = useState("100");
  const [rate, setRate] = useState("90");

  useEffect(() => {
    const updateProgress = () => {
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(
        scrollable > 0 ? Math.min(window.scrollY / scrollable, 1) : 0,
      );
    };
    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );
    document.querySelectorAll(".reveal").forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const validation = useMemo(
    () => validateCalculatorInputs(amount, rate),
    [amount, rate],
  );
  const result = validation.valid
    ? calculateOutcome(validation.amount, validation.payoutRate)
    : null;

  const steps = [
    {
      title: "注册并登录",
      text: "通过邮箱验证码或官网提供的钱包方式登录。若使用推荐链接，注册前确认网址中的 ref 参数仍然存在。",
      icon: "book" as IconName,
    },
    {
      title: "充值 USDT",
      text: "进入 Wallet / Deposit，选择网络后复制地址。转账前务必核对币种、网络和最小充值金额。",
      icon: "wallet" as IconName,
    },
    {
      title: "选择交易品种",
      text: "从交易对列表选择 BTC、ETH 或 XAUT，并观察图表、当前价格与近期波动。",
      icon: "trend" as IconName,
    },
    {
      title: "选择合约周期",
      text: "周期决定从入场到结算的等待时间。短周期噪音更大，不代表更容易预测。",
      icon: "clock" as IconName,
    },
    {
      title: "填写金额与方向",
      text: "确认可用余额、回报率和最大亏损后，选择 Higher 或 Lower。点击即可能提交真实订单。",
      icon: "calculator" as IconName,
    },
    {
      title: "查看持仓与结算",
      text: "在 Positions 查看倒计时、入场价和当前价；结算后到 History 核对结果与资金变化。",
      icon: "check" as IconName,
    },
  ];

  const faqs = [
    {
      question: "事件合约能保证盈利吗？",
      answer:
        "不能。任何方向判断都可能出错，判断错误时通常会损失该笔投入。历史结果、短期连胜或图表形态都不能保证未来表现。",
    },
    {
      question: "到期价刚好等于入场价怎么办？",
      answer:
        "根据本站在核验日期观察到的公开界面，平价会显示为 Draw，并退回该笔本金。实际结算规则请以下单时官网说明为准。",
    },
    {
      question: "为什么周期和回报率与这里不同？",
      answer:
        "平台会调整可用品种、周期、回报率和限额。本站展示的是日期快照，不是实时行情；下单前请重新读取官网交易面板。",
    },
    {
      question: "充值后没有到账怎么办？",
      answer:
        "先核对链上交易状态、币种、网络、充值地址和最低金额，再查看官网充值记录。确认均正确仍未到账时，应联系平台官方支持，切勿再次盲目转账。",
    },
    {
      question: "本站按钮包含推荐码吗？",
      answer:
        "包含。主按钮链接带有 ref=CAEC2977。链接提供者可能获得平台推荐权益；你也可以自行访问官网，不经本站推荐链接注册。",
    },
    {
      question: "L1 如何自动升级？",
      answer:
        "成功邀请 3 名有效用户后自动升级。每一名有效用户需要同时满足累计充值大于 50 USDT、累计交易量大于 500 USDT。最终统计结果以平台账户数据为准。",
    },
    {
      question: "本站能否直接自动操作真实 Bitevent 订单？",
      answer:
        "不能。本站的登录、订单和跟单功能均为浏览器内模拟，不读取 Bitevent 密码或 Cookie，不连接真实账户，也不会自动提交真实资金订单。未来只有在平台提供明确授权的官方 API 后，才适合设计受控接入。",
    },
  ];

  const handleNumberInput =
    (setter: (value: string) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setter(event.target.value);
    };

  return (
    <>
      <div
        className="scroll-progress"
        aria-hidden="true"
        style={{ transform: `scaleX(${scrollProgress})` }}
      />

      <header className="site-header">
        <div className="container nav-inner">
          <a className="brand" href="#top" aria-label="返回页面顶部">
            <span className="brand-mark">B</span>
            <span>
              Bitevent
              <small>交易指南</small>
            </span>
          </a>
          <nav aria-label="主要导航">
            <a href="#basics">合约原理</a>
            <a href="#guide">操作步骤</a>
            <a href="#rebate">返佣代理</a>
            <a href="#experience">模拟中心</a>
            <a href="#calculator">计算器</a>
          </nav>
          <InviteLink className="nav-cta">
            开始体验 <Icon name="external" size={15} />
          </InviteLink>
        </div>
      </header>

      <main id="main-content">
        <section className="hero" id="top">
          <div className="hero-grid" aria-hidden="true" />
          <div className="container hero-layout">
            <div className="hero-copy reveal">
              <div className="status-pill">
                <span />
                独立中文教程 · 已核验 {SITE_CONFIG.verifiedAtLabel}
              </div>
              <p className="hero-kicker">EVENT CONTRACT GUIDE</p>
              <h1>
                看懂方向，
                <br />
                再做<span>选择。</span>
              </h1>
              <p className="hero-intro">
                从 Higher / Lower 的结算逻辑，到充值、下单与风险管理，
                一页读懂 Bitevent 事件合约的基本操作。
              </p>
              <div className="hero-actions">
                <InviteLink>
                  使用邀请码开始体验 <Icon name="arrow" />
                </InviteLink>
                <a className="text-link" href="#guide">
                  先看完整教程 <span>↓</span>
                </a>
              </div>
              <p className="micro-disclaimer">
                <Icon name="warning" size={15} />
                非官方网站 · 非投资建议 · 交易可能损失全部投入
              </p>
            </div>

            <div className="terminal-card reveal" aria-label="方向合约交易界面示意图">
              <div className="terminal-topbar">
                <div>
                  <span className="terminal-logo">B</span>
                  <strong>交易演示</strong>
                  <span className="demo-badge">非实时</span>
                </div>
                <div className="terminal-tools" aria-hidden="true">
                  <span>◐</span>
                  <span>◎</span>
                </div>
              </div>

              <div className="terminal-workspace">
                <aside className="market-sidebar">
                  <p>交易对</p>
                  {[
                    ["₿", "BTC", "104,286.42", "+1.24%", "active"],
                    ["◆", "ETH", "3,425.18", "+0.86%", ""],
                    ["◈", "XAUT", "3,366.90", "-0.12%", "down"],
                  ].map(([symbol, name, price, change, state]) => (
                    <div className={`market-row ${state}`} key={name}>
                      <span>{symbol}</span>
                      <div>
                        <strong>{name}/USDT</strong>
                        <small>{price}</small>
                      </div>
                      <em>{change}</em>
                    </div>
                  ))}
                </aside>

                <div className="terminal-main">
                  <div className="ticker-bar">
                    <div className="ticker-symbol">
                      <span>₿</span>
                      <strong>BTC / USDT</strong>
                    </div>
                    <div>
                      <strong>104,286.42</strong>
                      <small>+1.24%</small>
                    </div>
                    <dl>
                      <div><dt>24h 最高</dt><dd>105,080.00</dd></div>
                      <div><dt>24h 最低</dt><dd>101,642.18</dd></div>
                    </dl>
                  </div>

                  <div className="terminal-content">
                    <div className="chart-panel">
                      <div className="chart-tabs">
                        <span>30s</span><span>1m</span><span className="active">5m</span>
                        <span>15m</span><span>1h</span>
                        <i>折线</i>
                      </div>
                      <div className="price-row">
                        <div>
                          <small>BTC/USDT · LOCAL</small>
                          <strong>104,286.42</strong>
                        </div>
                        <span className="price-change">+1.24%</span>
                      </div>
                      <div className="chart-area">
                        <svg viewBox="0 0 600 210" role="img" aria-label="紫色向上波动的示例价格曲线">
                          <defs>
                            <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#b847f9" stopOpacity=".32" />
                              <stop offset="100%" stopColor="#120719" stopOpacity=".05" />
                            </linearGradient>
                          </defs>
                          <path
                            className="chart-fill"
                            d="M0 176 C40 166 60 184 98 154 S155 170 198 131 S255 148 299 113 S355 132 397 89 S450 107 488 62 S545 78 600 24 L600 210 L0 210 Z"
                          />
                          <path
                            className="chart-line"
                            d="M0 176 C40 166 60 184 98 154 S155 170 198 131 S255 148 299 113 S355 132 397 89 S450 107 488 62 S545 78 600 24"
                          />
                          <line x1="0" y1="176" x2="600" y2="176" className="entry-line" />
                        </svg>
                        <span className="entry-tag">入场价</span>
                        <span className="live-point" />
                      </div>
                    </div>

                    <aside className="order-panel">
                      <p>合约周期</p>
                      <div className="period-grid">
                        <span className="active">30s</span><span>1m</span>
                        <span>5m</span><span>15m</span>
                      </div>
                      <p>开单金额</p>
                      <div className="mock-input"><strong>100</strong><span>USDT</span></div>
                      <div className="mock-presets"><span>5</span><span>10</span><span>100</span></div>
                      <div className="contract-options">
                        <div className="contract-option higher">
                          <span>↗ 看涨</span>
                          <small>回报率 90%</small>
                        </div>
                        <div className="contract-option lower">
                          <span>↘ 看跌</span>
                          <small>回报率 90%</small>
                        </div>
                      </div>
                    </aside>
                  </div>
                </div>
              </div>
              <p className="simulation-note">界面仅用于讲解原理，不连接账户、不提交订单，也不代表实时行情</p>
            </div>
          </div>
        </section>

        <section className="trust-strip" aria-label="本站原则">
          <div className="container trust-items">
            <span><Icon name="check" /> 不承诺收益</span>
            <span><Icon name="check" /> 参数注明日期</span>
            <span><Icon name="check" /> 风险先于操作</span>
            <span><Icon name="check" /> 推荐关系透明</span>
          </div>
        </section>

        <section className="section" id="basics">
          <div className="container">
            <SectionHeading
              eyebrow="01 · 先懂原理"
              title="你不是在买币，而是在判断到期方向"
              description="事件合约把一次价格判断压缩到明确的开始与结束时间。四个概念先弄清楚，再考虑是否参与。"
            />
            <div className="concept-grid">
              {[
                ["01", "入场价", "订单成功提交瞬间记录的参考价格，是最终比较的起点。"],
                ["02", "合约周期", "从下单到事件结束的时间，例如 30 秒、5 分钟或 1 小时。"],
                ["03", "到期价", "周期结束时用于结算的价格，与入场价比较决定结果。"],
                ["04", "回报率", "判断正确时的净利润比例。90% 代表投入 100，净利润为 90。"],
              ].map(([number, title, text]) => (
                <article className="concept-card reveal" key={number}>
                  <span className="concept-number">{number}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>

            <div className="direction-explainer reveal">
              <div className="direction-side up">
                <span className="direction-icon">↗</span>
                <p>到期价 &gt; 入场价</p>
                <strong>HIGHER 获胜</strong>
                <small>预测价格会上涨</small>
              </div>
              <div className="direction-center">
                <span>入场价</span>
                <strong>104,286</strong>
                <div />
                <p>到期时比较</p>
              </div>
              <div className="direction-side down">
                <span className="direction-icon">↘</span>
                <p>到期价 &lt; 入场价</p>
                <strong>LOWER 获胜</strong>
                <small>预测价格会下跌</small>
              </div>
              <div className="draw-note">
                <span>=</span>
                若到期价与入场价相同，界面显示 Draw，本金退回
              </div>
            </div>
          </div>
        </section>

        <section className="section section-alt" id="guide">
          <div className="container">
            <SectionHeading
              eyebrow="02 · 操作路径"
              title="从注册到结算，六步走完一笔交易"
              description="按钮名称可能随官网更新略有变化。任何涉及资金的操作，都以你当时看到的官网页面为准。"
            />
            <div className="steps">
              {steps.map((step, index) => (
                <article className="step-card reveal" key={step.title}>
                  <div className="step-rail">
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    {index < steps.length - 1 && <i />}
                  </div>
                  <div className="step-icon">
                    <Icon name={step.icon} />
                  </div>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                    {index === 1 && (
                      <div className="inline-warning">
                        <Icon name="warning" size={16} />
                        不要把其他币种或不支持的网络转入充值地址
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>

            <div className="guide-cta reveal">
              <div>
                <p className="eyebrow">READY TO EXPLORE?</p>
                <h3>带着规则和风险意识，再打开交易页面</h3>
                <p>推荐参数 CAEC2977 已包含在链接中。</p>
              </div>
              <InviteLink>
                前往 Bitevent 官网 <Icon name="external" />
              </InviteLink>
            </div>
          </div>
        </section>

        <section className="section" id="snapshot">
          <div className="container">
            <SectionHeading
              eyebrow="03 · 参数快照"
              title="本站核验时，官网开放了这些市场"
              description={`以下信息核验于 ${SITE_CONFIG.verifiedAtLabel}，不是实时数据。平台可能随时调整品种、周期、回报率与限额。`}
            />
            <div className="snapshot-table reveal">
              <div className="snapshot-header">
                <span>交易品种</span>
                <span>可用周期</span>
                <span>当日公开回报率快照</span>
              </div>
              {MARKET_SNAPSHOT.map((market) => (
                <div className="snapshot-row" key={market.symbol}>
                  <div className="asset-cell">
                    <span
                      className="asset-badge"
                      style={{ "--asset-color": market.accent } as React.CSSProperties}
                    >
                      {market.symbol.slice(0, 1)}
                    </span>
                    <div>
                      <strong>{market.symbol} / USDT</strong>
                      <small>{market.name}</small>
                    </div>
                  </div>
                  <div className="period-tags">
                    {market.periods.map((period) => (
                      <span key={period}>{period}</span>
                    ))}
                  </div>
                  <p>{market.rateNote}</p>
                </div>
              ))}
              <div className="snapshot-footer">
                <Icon name="clock" size={17} />
                快照用于帮助理解页面，不应作为下单依据。请读取官网交易面板的当前数值。
              </div>
            </div>
          </div>
        </section>

        <RebateProgram />

        <MemberExperience />

        <section className="section calculator-section" id="calculator">
          <div className="container calculator-layout">
            <div>
              <SectionHeading
                eyebrow="06 · 盈亏计算器"
                title="下单之前，先算清楚最坏结果"
                description="输入交易面板当前显示的金额与回报率。计算结果不包含平台可能存在的其他费用或规则变化。"
              />
              <div className="formula-card reveal">
                <span>计算公式</span>
                <p>净利润 = 交易金额 × 回报率</p>
                <p>总返还 = 本金 + 净利润</p>
                <p>盈亏平衡胜率 = 1 ÷（1 + 回报率）</p>
              </div>
            </div>

            <div className="calculator-card reveal">
              <div className="calculator-header">
                <span><Icon name="calculator" /> 场景计算</span>
                <small>DEMO</small>
              </div>
              <div className="input-grid">
                <label>
                  <span>交易金额</span>
                  <div className={validation.valid || !validation.amountError ? "input-shell" : "input-shell invalid"}>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max="1000000000"
                      step="0.01"
                      value={amount}
                      onChange={handleNumberInput(setAmount)}
                      aria-describedby="amount-error"
                    />
                    <strong>USDT</strong>
                  </div>
                  <small id="amount-error" className="field-error">
                    {!validation.valid ? validation.amountError : ""}
                  </small>
                </label>
                <label>
                  <span>官网显示的回报率</span>
                  <div className={validation.valid || !validation.rateError ? "input-shell" : "input-shell invalid"}>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max="100"
                      step="0.01"
                      value={rate}
                      onChange={handleNumberInput(setRate)}
                      aria-describedby="rate-error"
                    />
                    <strong>%</strong>
                  </div>
                  <small id="rate-error" className="field-error">
                    {!validation.valid ? validation.rateError : ""}
                  </small>
                </label>
              </div>

              <div className="result-grid" aria-live="polite">
                <div className="result-card positive">
                  <span>预测正确 · 净利润</span>
                  <strong>
                    +{result ? formatUsdt(result.netProfit) : "—"}{" "}
                    <small>USDT</small>
                  </strong>
                </div>
                <div className="result-card">
                  <span>预测正确 · 总返还</span>
                  <strong>
                    {result ? formatUsdt(result.totalReturn) : "—"}{" "}
                    <small>USDT</small>
                  </strong>
                </div>
                <div className="result-card negative">
                  <span>预测错误 · 最大亏损</span>
                  <strong>
                    -{result ? formatUsdt(result.maxLoss) : "—"}{" "}
                    <small>USDT</small>
                  </strong>
                </div>
                <div className="result-card">
                  <span>理论盈亏平衡胜率</span>
                  <strong>
                    {result ? (result.breakEvenRate * 100).toFixed(2) : "—"}
                    <small>%</small>
                  </strong>
                </div>
              </div>
              <p className="calculator-note">
                这是数学演示，不是收益预测。盈亏平衡胜率未计入执行差异、规则变化与行为偏差。
              </p>
            </div>
          </div>
        </section>

        <section className="section risk-section" id="risk">
          <div className="container">
            <SectionHeading
              eyebrow="07 · 风险管理"
              title="真正重要的，不是猜对一次"
              description="事件合约的结果快、反馈强，很容易诱发冲动决策。先设边界，比寻找所谓的“必胜信号”更重要。"
            />
            <div className="risk-layout">
              <div className="risk-list">
                {RISKS.map((risk, index) => (
                  <article className="risk-item reveal" key={risk.title}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <div>
                      <h3>{risk.title}</h3>
                      <p>{risk.text}</p>
                    </div>
                  </article>
                ))}
              </div>
              <aside className="risk-promise reveal">
                <div className="shield-orbit">
                  <Icon name="shield" size={38} />
                </div>
                <p className="eyebrow">下单前检查</p>
                <h3>给自己一个明确的停止条件</h3>
                <ul>
                  <li><Icon name="check" size={17} /> 单笔最多损失多少？</li>
                  <li><Icon name="check" size={17} /> 当天亏到多少就停止？</li>
                  <li><Icon name="check" size={17} /> 是否读懂本次结算条件？</li>
                  <li><Icon name="check" size={17} /> 这笔钱能否承受全部损失？</li>
                </ul>
                <p className="risk-quote">
                  “不知道何时停止”的策略，不是完整策略。
                </p>
              </aside>
            </div>
          </div>
        </section>

        <section className="section faq-section" id="faq">
          <div className="container faq-layout">
            <SectionHeading
              eyebrow="08 · 常见问题"
              title="下单前，最好再确认这些"
            />
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <details className="faq-item reveal" key={faq.question} open={index === 0}>
                  <summary>
                    <span>{faq.question}</span>
                    <i aria-hidden="true">+</i>
                  </summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="final-cta">
          <div className="final-orb" aria-hidden="true" />
          <div className="container final-cta-inner reveal">
            <p className="eyebrow">RULES FIRST. DECISION SECOND.</p>
            <h2>读懂规则之后，再决定是否参与。</h2>
            <p>
              如果你已理解结算方式与最大风险，可通过推荐链接前往 Bitevent。
            </p>
            <InviteLink>
              使用邀请码 CAEC2977 <Icon name="external" />
            </InviteLink>
            <small>你也可以直接访问官网，不使用推荐链接。</small>
          </div>
        </section>
      </main>

      <footer>
        <div className="container footer-top">
          <div>
            <a className="brand footer-brand" href="#top">
              <span className="brand-mark">B</span>
              <span>Bitevent<small>交易指南</small></span>
            </a>
            <p>{SITE_CONFIG.disclosure}</p>
          </div>
          <div className="footer-links">
            <div>
              <strong>快速导航</strong>
              <a href="#basics">合约原理</a>
              <a href="#guide">操作步骤</a>
              <a href="#rebate">返佣代理</a>
              <a href="#experience">模拟中心</a>
              <a href="#calculator">盈亏计算</a>
            </div>
            <div>
              <strong>站外链接</strong>
              <a
                href={SITE_CONFIG.officialUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Bitevent 官网 <Icon name="external" size={13} />
              </a>
              <InviteLink className="footer-invite">
                推荐注册链接 <Icon name="external" size={13} />
              </InviteLink>
            </div>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>参数最后核验：{SITE_CONFIG.verifiedAt}</span>
          <span>© 2026 Bitevent 交易指南 · 独立教程 · 非投资建议</span>
        </div>
      </footer>
    </>
  );
}

export default App;
